import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Application } from '@feathersjs/feathers';
import { useFeathers } from '../../app/util';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { User } from '../../app/feathers/users/User';
import { InviteLink } from '../../app/feathers/invite-links/InviteLink';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TablePagination from '@mui/material/TablePagination';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SchoolIcon from '@mui/icons-material/School';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import FilterListIcon from '@mui/icons-material/FilterList';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import {
  ResearcherLevelBadge,
  CircularProgressBar,
  UserAvatar,
  Title,
} from '../../components';
import type { TrainingEntry } from '../../app/feathers/users/User';
import { PerformanceColumn } from './PerformanceColumn';
import EditPermissionsModal from '../../components/PermissionsModal/EditPermissionsModal';
import SendInvitationModal from '../../components/PermissionsModal/SendInvitationModal';
import BulkInviteResearchersModal from '../../components/PermissionsModal/BulkInviteResearchersModal';

const TEAM_TAB = 'team';
const EXTERNAL_ACCESS_TAB = 'external-access';
const INVITES_TAB = 'invites';
const INVITES_PAGE_SIZE = 10;
const TEAM_ROWS_PER_PAGE_OPTIONS = [25];
const EXTERNAL_ROWS_PER_PAGE_OPTIONS = [25];
const TEAM_PERMISSIONS = [
  'researcher',
  'reviewer',
  'editor',
  'super-admin',
  'admin',
  'publisher',
];

/** Threshold: missingDataPercent >= this counts as "low info" candidate. */
const LOW_INFO_THRESHOLD = 50;

function getCurrentTrainingLevel(user: User): number | null {
  const publisherLevel = user.publisherLevel ?? 0;
  const researcherLevel = user.researcherLevel ?? 1;
  if (publisherLevel >= 3) return null;
  return Math.min(publisherLevel + 1, researcherLevel);
}

function isReadyForUpgrade(user: User, level: number): boolean {
  const total = (user.trainingTotalCompletedCountAtLevel || {})[level] ?? 0;
  const allowed = (user.trainingAllowedCandidatesAtLevel || {})[level] ?? 0;
  return total >= allowed;
}

const Wrapper = styled.div`
  width: calc(100% - 24px - 36px);
  display: flex;
  flex-direction: column;
  padding: 36px 36px 36px 24px;
  align-items: stretch;
  min-width: 800px;
`;

const ProgressGauges = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
`;

const StyledFilterButton = muiStyled(Button, {
  shouldForwardProp: (prop) => prop !== 'hasValue',
})<{ hasValue?: boolean }>(({ theme, hasValue }) => ({
  textTransform: 'none',
  color: hasValue ? theme.palette.primary.contrastText : theme.palette.text.primary,
  borderColor: theme.palette.divider,
  backgroundColor: hasValue ? theme.palette.primary.main : theme.palette.common.white,
  borderRadius: '9999px',
  borderWidth: '1px',
  borderStyle: 'solid',
  boxShadow: 'none',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: hasValue ? theme.palette.primary.dark : theme.palette.primary.light,
    color: hasValue ? theme.palette.primary.contrastText : theme.palette.text.primary,
    boxShadow: 'none',
  },
}));

function LowInfoLine({ list }: { list: TrainingEntry[] }) {
  if (list.length === 0) return null;
  const lowInfoCount = list.filter(
    (e) => (e.missingDataPercent ?? 0) >= LOW_INFO_THRESHOLD
  ).length;
  const lowInfoPct = Math.round((lowInfoCount / list.length) * 100);
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 1 }}>
      {lowInfoPct}% of candidates were low info
    </Typography>
  );
}

const Team: React.FC = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [researchers, setResearchers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [teamPage, setTeamPage] = useState(0);
  const [teamRowsPerPage, setTeamRowsPerPage] = useState(25);
  const [teamTotalCount, setTeamTotalCount] = useState(0);
  const [externalAccessUsers, setExternalAccessUsers] = useState<User[]>([]);
  const [externalAccessLoading, setExternalAccessLoading] = useState(true);
  const [externalPage, setExternalPage] = useState(0);
  const [externalRowsPerPage, setExternalRowsPerPage] = useState(25);
  const [externalTotalCount, setExternalTotalCount] = useState(0);
  const [inviteLinks, setInviteLinks] = useState<InviteLink[]>([]);
  const [inviteLinksLoading, setInviteLinksLoading] = useState(true);
  const [inviteLinksPage, setInviteLinksPage] = useState(0);
  const [inviteActionLoading, setInviteActionLoading] = useState<string | null>(null);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [moreMenuUser, setMoreMenuUser] = useState<User | null>(null);
  const [moreMenuContext, setMoreMenuContext] = useState<'team' | 'external' | null>(null);
  const [editPermissionsUser, setEditPermissionsUser] = useState<User | null>(null);
  const [editPermissionsAuditorMode, setEditPermissionsAuditorMode] = useState(false);
  const [removePermissionsUser, setRemovePermissionsUser] = useState<User | null>(null);
  const [removePermissionsContext, setRemovePermissionsContext] = useState<'team' | 'external' | null>(null);
  const [showSendInvitationModal, setShowSendInvitationModal] = useState(false);
  const [showBulkInviteModal, setShowBulkInviteModal] = useState(false);
  const [sendInvitationAuditorMode, setSendInvitationAuditorMode] = useState(false);
  const [activeTab, setActiveTab] = useState(TEAM_TAB);
  const [stateNameById, setStateNameById] = useState<Record<string, string>>({});
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [trainingFilter, setTrainingFilter] = useState<boolean | null>(null);
  const [trainingFilterAnchorEl, setTrainingFilterAnchorEl] = useState<null | HTMLElement>(null);

  const fetchResearchers = useCallback(async () => {
    if (!feathers) return;
    setLoading(true);
    try {
      const result = await feathers.getService('users').find({
        query: {
          $and: [
            { permissions: { $in: TEAM_PERMISSIONS } },
            { permissions: { $nin: ['test'] } },
          ],
          ...(trainingFilter === true ? { isInTraining: true } : {}),
          $sort: { createdAt: -1 },
          $skip: teamPage * teamRowsPerPage,
          $limit: teamRowsPerPage,
        },
      });
      const { data, total } = result as { data?: User[]; total?: number };
      setResearchers((data || []) as User[]);
      setTeamTotalCount(typeof total === 'number' ? total : (data || []).length);
    } catch (err) {
      console.error('Failed to fetch researchers', err);
    } finally {
      setLoading(false);
    }
  }, [feathers, teamPage, teamRowsPerPage, trainingFilter]);

  const fetchExternalAccessUsers = useCallback(async () => {
    if (!feathers) return;
    setExternalAccessLoading(true);
    try {
      const result = await feathers.getService('users').find({
        query: {
          $and: [
            { permissions: { $in: ['auditor'] } },
            { permissions: { $nin: ['test'] } },
          ],
          $sort: { createdAt: -1 },
          $skip: externalPage * externalRowsPerPage,
          $limit: externalRowsPerPage,
        },
      });
      const { data, total } = result as { data?: User[]; total?: number };
      const users = (data || []) as User[];
      setExternalAccessUsers(users);
      setExternalTotalCount(typeof total === 'number' ? total : users.length);

      const stateIds = users
        .flatMap((user) => user.limitAccessToStates || [])
        .filter(Boolean)
        .map((state) => `${state}`);
      const uniqueStateIds = [...new Set(stateIds)];
      if (uniqueStateIds.length > 0) {
        const { data: states } = await feathers.getService('districts').find({
          query: {
            _id: { $in: uniqueStateIds },
            $limit: uniqueStateIds.length,
          },
        });
        const nextMap = (states || []).reduce((acc: Record<string, string>, state: unknown) => {
          const district = state as { _id?: string; longName?: string; name?: string; text?: string };
          if (district?._id) {
            acc[district._id] = district.longName || district.name || district.text || district._id;
          }
          return acc;
        }, {});
        setStateNameById((prev) => ({ ...prev, ...nextMap }));
      }
    } catch (err) {
      console.error('Failed to fetch external access users', err);
    } finally {
      setExternalAccessLoading(false);
    }
  }, [feathers, externalPage, externalRowsPerPage]);

  const fetchInviteLinks = useCallback(async () => {
    if (!feathers) return;
    setInviteLinksLoading(true);
    try {
      const { data } = await feathers.getService('invite-links').find({
        query: {
          type: 'data-editor',
          valid: true,
          $sort: { createdAt: -1 },
          $limit: 500,
        },
      });
      const links = (data || []) as InviteLink[];
      setInviteLinks(links);
      setInviteLinksPage(0);

      const stateIds = links
        .flatMap((invite) => invite.limitAccessToStates || [])
        .filter(Boolean)
        .map((state) => `${state}`);
      const uniqueStateIds = [...new Set(stateIds)];
      if (uniqueStateIds.length > 0) {
        const { data: states } = await feathers.getService('districts').find({
          query: {
            _id: { $in: uniqueStateIds },
            $limit: uniqueStateIds.length,
          },
        });
        const nextMap = (states || []).reduce((acc: Record<string, string>, state: unknown) => {
          const district = state as { _id?: string; longName?: string; name?: string; text?: string };
          if (district?._id) {
            acc[district._id] = district.longName || district.name || district.text || district._id;
          }
          return acc;
        }, {});
        setStateNameById((prev) => ({ ...prev, ...nextMap }));
      }
    } catch (err) {
      console.error('Failed to fetch invite links', err);
    } finally {
      setInviteLinksLoading(false);
    }
  }, [feathers]);

  useEffect(() => {
    fetchResearchers();
  }, [fetchResearchers]);

  useEffect(() => {
    if (activeTab === EXTERNAL_ACCESS_TAB) {
      fetchExternalAccessUsers();
    }
  }, [activeTab, fetchExternalAccessUsers]);

  useEffect(() => {
    if (activeTab === INVITES_TAB) {
      fetchInviteLinks();
    }
  }, [activeTab, fetchInviteLinks]);

  const getPendingReviewCount = (user: User) => {
    const currentLevel = getCurrentTrainingLevel(user);
    if (currentLevel == null) return 0;
    return (user.trainingPendingCountAtLevel || {})[currentLevel] ?? 0;
  };

  const handleOpenTrainingFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTrainingFilterAnchorEl(event.currentTarget);
  };

  const handleCloseTrainingFilter = () => {
    setTrainingFilterAnchorEl(null);
  };

  const handleSetTrainingFilter = (value: boolean | null) => {
    setTrainingFilter(value);
    setTeamPage(0);
    handleCloseTrainingFilter();
  };

  const handleAssignMore = async (user: User, level: 1 | 2 | 3) => {
    setActionLoading(user._id);
    try {
      await feathers!.getService('users').patch(user._id, {
        trainingAssignMoreAtLevel: { level, amount: 10 },
      });
      await fetchResearchers();
    } catch (err) {
      console.error('Assign more failed', err);
    } finally {
      setActionLoading(null);
    }
  };

  console.log('users', researchers);

  const handlePromote = async (user: User, level: 1 | 2 | 3) => {
    setActionLoading(user._id);
    try {
      await feathers!.getService('users').patch(user._id, {
        publisherLevel: level,
        researcherLevel: Math.max(user.researcherLevel === undefined ? 1 : user.researcherLevel, level + 1) as 1 | 2 | 3,
      });
      await fetchResearchers();
    } catch (err) {
      console.error('Promote failed', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelInvite = async (invite: InviteLink) => {
    if (!feathers || !invite?._id) return;
    const previousInviteLinks = inviteLinks;
    const previousInviteLinksPage = inviteLinksPage;
    const nextInviteCount = Math.max(0, inviteLinks.length - 1);
    const maxPage = Math.max(0, Math.ceil(nextInviteCount / INVITES_PAGE_SIZE) - 1);

    setInviteLinks((prev) => prev.filter((item) => item._id !== invite._id));
    setInviteLinksPage((prev) => Math.min(prev, maxPage));
    setInviteActionLoading(invite._id);
    try {
      await feathers.getService('invite-links').patch(
        invite._id,
        { valid: false },
        { query: { email: invite.email, type: 'data-editor' } }
      );
    } catch (err) {
      console.error('Failed to cancel invite', err);
      setInviteLinks(previousInviteLinks);
      setInviteLinksPage(previousInviteLinksPage);
    } finally {
      setInviteActionLoading(null);
    }
  };

  const handleOpenMoreMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    user: User,
    context: 'team' | 'external'
  ) => {
    event.stopPropagation();
    setMoreMenuAnchorEl(event.currentTarget);
    setMoreMenuUser(user);
    setMoreMenuContext(context);
  };

  const handleCloseMoreMenu = () => {
    setMoreMenuAnchorEl(null);
    setMoreMenuUser(null);
    setMoreMenuContext(null);
  };

  const handleOpenEditUser = () => {
    if (!moreMenuUser) return;
    setEditPermissionsAuditorMode(moreMenuContext === 'external');
    setEditPermissionsUser(moreMenuUser);
    handleCloseMoreMenu();
  };

  const handleOpenRemoveUser = () => {
    if (!moreMenuUser) return;
    setRemovePermissionsUser(moreMenuUser);
    setRemovePermissionsContext(moreMenuContext);
    handleCloseMoreMenu();
  };

  const handleRemovePermissions = async (user: User) => {
    const previousResearchers = researchers;
    const previousExternalAccessUsers = externalAccessUsers;
    const previousTeamTotalCount = teamTotalCount;
    const previousExternalTotalCount = externalTotalCount;
    if (removePermissionsContext === 'external') {
      setExternalAccessUsers((prev) => prev.filter((externalUser) => externalUser._id !== user._id));
      setExternalTotalCount((prev) => Math.max(0, prev - 1));
    } else {
      setResearchers((prev) => prev.filter((researcher) => researcher._id !== user._id));
      setTeamTotalCount((prev) => Math.max(0, prev - 1));
    }
    try {
      await feathers!.getService('users').patch(user._id, { permissions: [] });
    } catch (err) {
      console.error('Failed to remove user permissions', err);
      setResearchers(previousResearchers);
      setExternalAccessUsers(previousExternalAccessUsers);
      setTeamTotalCount(previousTeamTotalCount);
      setExternalTotalCount(previousExternalTotalCount);
    } finally {
      setRemovePermissionsUser(null);
      setRemovePermissionsContext(null);
    }
  };

  const handleOpenSendInvitation = () => {
    if (activeTab === EXTERNAL_ACCESS_TAB) {
      setSendInvitationAuditorMode(true);
    } else {
      setSendInvitationAuditorMode(false);
    }
    setShowSendInvitationModal(true);
  };

  const getUserDisplayName = (user: User) =>
    user.name ||
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.email ||
    '—';

  const getLimitedStateLabels = (stateIds?: string[]) => {
    if (!stateIds || stateIds.length === 0) return 'All states';
    return stateIds
      .map((stateId) => stateNameById[stateId] || stateId)
      .join(', ');
  };

  if (loading && activeTab === TEAM_TAB) {
    return (
      <Wrapper>
        <Title>User management</Title>
        <Typography variant="h1">Team</Typography>
        <Typography variant="body1">Loading…</Typography>
      </Wrapper>
    );
  }

  console.log('researchers', researchers);

  return (
    <Wrapper>
      <Title>User management</Title>
      <Typography variant="h1">User management</Typography>
      <Modal
        open={showSendInvitationModal}
        onClose={() => setShowSendInvitationModal(false)}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 1,
            minWidth: 320,
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          <SendInvitationModal
            auditorMode={sendInvitationAuditorMode}
            refreshUsers={() => {
              fetchResearchers();
              fetchExternalAccessUsers();
              fetchInviteLinks();
            }}
          />
        </Box>
      </Modal>
      <Modal
        open={showBulkInviteModal}
        onClose={() => setShowBulkInviteModal(false)}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 1,
            minWidth: 320,
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          <BulkInviteResearchersModal
            onClose={() => setShowBulkInviteModal(false)}
            refreshUsers={() => {
              fetchResearchers();
              fetchInviteLinks();
            }}
          />
        </Box>
      </Modal>
      <Modal
        open={Boolean(editPermissionsUser)}
        onClose={() => {
          setEditPermissionsUser(null);
          setEditPermissionsAuditorMode(false);
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 1,
            minWidth: 320,
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          {editPermissionsUser && (
            <EditPermissionsModal
              user={editPermissionsUser}
              auditorMode={editPermissionsAuditorMode}
              refreshUsers={() => {
                fetchResearchers();
                fetchExternalAccessUsers();
                setEditPermissionsUser(null);
                setEditPermissionsAuditorMode(false);
              }}
            />
          )}
        </Box>
      </Modal>
      <Menu
        anchorEl={moreMenuAnchorEl}
        open={Boolean(moreMenuAnchorEl)}
        onClose={handleCloseMoreMenu}
      >
        {moreMenuContext === 'team' &&
          moreMenuUser &&
          getPendingReviewCount(moreMenuUser) > 0 && (
            <MenuItem
              component={Link}
              to={`/tasks/publish-candidate?user=${encodeURIComponent(moreMenuUser._id)}`}
              onClick={handleCloseMoreMenu}
            >
              {`Review tasks (${getPendingReviewCount(moreMenuUser)})`}
            </MenuItem>
          )}
        <MenuItem onClick={handleOpenEditUser}>Edit user</MenuItem>
        <MenuItem onClick={handleOpenRemoveUser}>Remove user</MenuItem>
      </Menu>
      <Dialog
        open={Boolean(removePermissionsUser)}
        onClose={() => setRemovePermissionsUser(null)}
      >
        <DialogTitle>
          {`Are you sure you want to remove ${removePermissionsUser?.email || 'this user'}?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Selecting 'Remove User' will remove ${removePermissionsUser?.email || 'this user'} from the research portal. You will need to send them another invitation in order for them to return.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovePermissionsUser(null)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => removePermissionsUser && handleRemovePermissions(removePermissionsUser)}
            color="primary"
          >
            Remove User
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mt: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{ mb: 0, flex: 1, minWidth: 0 }}
        >
          <Tab label="Team" value={TEAM_TAB} />
          <Tab label="External Access" value={EXTERNAL_ACCESS_TAB} />
          <Tab label="Invites" value={INVITES_TAB} />
        </Tabs>
        {activeTab === TEAM_TAB && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Bulk upload invites">
              <IconButton
                color="primary"
                onClick={() => setShowBulkInviteModal(true)}
                sx={{
                  border: 1,
                  borderColor: 'primary.main',
                  borderRadius: 1,
                }}
              >
                <UploadFileIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenSendInvitation}
            >
              Add user
            </Button>
          </Box>
        )}
        {activeTab === EXTERNAL_ACCESS_TAB && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenSendInvitation}
          >
            Add auditor
          </Button>
        )}
      </Box>

      {activeTab === TEAM_TAB && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
          <StyledFilterButton
            hasValue={trainingFilter !== null}
            variant={trainingFilter !== null ? 'contained' : 'outlined'}
            onClick={handleOpenTrainingFilter}
            startIcon={<FilterListIcon />}
            endIcon={trainingFilter !== null ? (
              <CancelIcon
                onClick={(event) => {
                  event.stopPropagation();
                  handleSetTrainingFilter(null);
                }}
              />
            ) : undefined}
            size="small"
          >
            {trainingFilter === true ? 'In training' : 'Training'}
          </StyledFilterButton>
          <Popover
            open={Boolean(trainingFilterAnchorEl)}
            anchorEl={trainingFilterAnchorEl}
            onClose={handleCloseTrainingFilter}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <List sx={{ minWidth: 220 }}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleSetTrainingFilter(null)}>
                  <Checkbox edge="start" checked={trainingFilter === null} tabIndex={-1} disableRipple />
                  <ListItemText primary="All users" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleSetTrainingFilter(true)}>
                  <Checkbox edge="start" checked={trainingFilter === true} tabIndex={-1} disableRipple />
                  <ListItemText primary="In training" />
                </ListItemButton>
              </ListItem>
            </List>
          </Popover>
        </Box>
      )}

      {activeTab === TEAM_TAB && (
        <TableContainer component={Paper} sx={{ marginTop: 3 }}>
          <Table size="small" aria-label="Team users" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '18%', maxWidth: 240 }}>User</TableCell>
                <TableCell sx={{ width: '10%', maxWidth: 100 }}>Level</TableCell>
                <TableCell sx={{ width: '24%' }}>Training progress</TableCell>
                <TableCell sx={{ width: '30%' }}>Performance</TableCell>
                <TableCell align="right" sx={{ width: '18%' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {researchers.map((user) => {
              const inTraining = user.isInTraining === true;
              const currentLevel = getCurrentTrainingLevel(user);
              const ready =
                inTraining &&
                currentLevel != null &&
                isReadyForUpgrade(user, currentLevel);
              const total =
                (user.trainingTotalCompletedCountAtLevel || {})[
                  currentLevel as number
                ] ?? 0;
              const allowed =
                (user.trainingAllowedCandidatesAtLevel || {})[
                  currentLevel as number
                ] || 1;
              const pending =
                (user.trainingPendingCountAtLevel || {})[
                  currentLevel as number
                ] ?? 0;
              const reviewed = Math.max(0, (total ?? 0) - (pending ?? 0));
              const completedPct =
                allowed > 0 ? Math.min(100, (total / allowed) * 100) : 0;
              const reviewedPct =
                allowed > 0 ? Math.min(100, (reviewed / allowed) * 100) : 0;
              const busy = actionLoading === user._id

              const displayName = getUserDisplayName(user);

              const showActions =
                inTraining && ready && currentLevel != null;

              const pendingReviewCount = getPendingReviewCount(user);

                return (
                  <TableRow key={user._id} hover>
                  <TableCell sx={{ width: '18%', maxWidth: 240 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'nowrap',
                      }}
                    >
                      <UserAvatar user={user} size={36} />
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 0,
                            maxWidth: '100%',
                          }}
                        >
                          <Typography variant="body1" component="span" noWrap>
                            {displayName}
                          </Typography>
                          {inTraining && (
                            <Tooltip title="In training">
                              <SchoolIcon
                                fontSize="small"
                                sx={{ color: 'primary.main' }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        {user.email && (
                          <Typography
                            component="a"
                            href={`mailto:${user.email}`}
                            variant="body2"
                            color="textSecondary"
                            display="block"
                            noWrap
                            sx={{ textDecoration: 'none' }}
                          >
                            {user.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '12%', maxWidth: 120 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <ResearcherLevelBadge
                        researcherLevel={user.researcherLevel}
                        publisherLevel={user.publisherLevel}
                        permissions={user.permissions}
                        style={{ height: 30, width: 30 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '38%' }}>
                    {inTraining && currentLevel != null ? (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0, ml: 1, mt: 1 }}>
                          {currentLevel < 3
                            ? `Progress to level ${currentLevel + 1}`
                            : 'Progress to completing training'}
                        </Typography>
                        <ProgressGauges>
                        <CircularProgressBar
                          style={{ transform: 'scale(0.8)' }}
                          size={70}
                          percentage={completedPct}
                          subtext={'submitted'}
                          dataTip={`${total} of ${allowed} submitted`}
                        />
                        <CircularProgressBar
                          style={{ transform: 'scale(0.8)' }}
                          size={70}
                          percentage={reviewedPct}
                          subtext={'reviewed'}
                          dataTip={`${reviewed} of ${allowed} reviewed`}
                        />
                      </ProgressGauges>
                        <LowInfoLine
                          list={user.trainingCompletedAtLevel?.[currentLevel] ?? []}
                        />
                      </Box>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ width: '22%' }}>
                    {inTraining && currentLevel != null ? (
                      <PerformanceColumn
                        list={user.trainingCompletedAtLevel?.[currentLevel] ?? []}
                        trainingLevel={currentLevel}
                        trainingDataByLevel={user.trainingCompletedAtLevel}
                      />
                    ) : (
                      <Typography color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ width: '18%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                      }}
                    >
                      {showActions ? (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busy}
                            onClick={() =>
                              handleAssignMore(user, currentLevel as 1 | 2 | 3)
                            }
                          >
                            Assign more work
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={busy}
                            onClick={() =>
                              handlePromote(user, currentLevel as 1 | 2 | 3)
                            }
                          >
                            Promote to level {currentLevel + 1}
                          </Button>
                        </>
                      ) : null}
                      <Tooltip title="More actions" placement="top">
                        <IconButton
                          size="small"
                          onClick={(event) => handleOpenMoreMenu(event, user, 'team')}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={teamTotalCount}
            page={teamPage}
            onPageChange={(_, page) => setTeamPage(page)}
            rowsPerPage={teamRowsPerPage}
            onRowsPerPageChange={(event) => {
              setTeamRowsPerPage(parseInt(event.target.value, 10));
              setTeamPage(0);
            }}
            rowsPerPageOptions={TEAM_ROWS_PER_PAGE_OPTIONS}
          />
        </TableContainer>
      )}

      {activeTab === EXTERNAL_ACCESS_TAB && (
        <TableContainer component={Paper} sx={{ marginTop: 3 }}>
          <Table size="small" aria-label="External access users" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '28%' }}>User</TableCell>
                <TableCell sx={{ width: '67%' }}>Limited to states</TableCell>
                <TableCell sx={{ width: '5%' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {externalAccessLoading ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2">Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : externalAccessUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2">No external access users found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                externalAccessUsers.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <UserAvatar user={user} size={36} />
                        <Box>
                          <Typography variant="body1">{getUserDisplayName(user)}</Typography>
                          {user.email && (
                            <Typography
                              component="a"
                              href={`mailto:${user.email}`}
                              variant="body2"
                              color="textSecondary"
                              sx={{ textDecoration: 'none' }}
                            >
                              {user.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getLimitedStateLabels(user.limitAccessToStates?.map((state) => `${state}`))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="More actions" placement="top">
                        <IconButton
                          size="small"
                          onClick={(event) => handleOpenMoreMenu(event, user, 'external')}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={externalTotalCount}
            page={externalPage}
            onPageChange={(_, page) => setExternalPage(page)}
            rowsPerPage={externalRowsPerPage}
            onRowsPerPageChange={(event) => {
              setExternalRowsPerPage(parseInt(event.target.value, 10));
              setExternalPage(0);
            }}
            rowsPerPageOptions={EXTERNAL_ROWS_PER_PAGE_OPTIONS}
          />
        </TableContainer>
      )}

      {activeTab === INVITES_TAB && (
        <TableContainer component={Paper} sx={{ marginTop: 3 }}>
          {inviteLinksLoading ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2">Loading...</Typography>
            </Box>
          ) : inviteLinks.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2">No active invites.</Typography>
            </Box>
          ) : (
            <>
              <Table size="small" aria-label="Active invite links" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '35%' }}>Email</TableCell>
                    <TableCell sx={{ width: '40%' }}>Limited to states</TableCell>
                    <TableCell sx={{ width: '20%' }}>Invite sent</TableCell>
                    <TableCell sx={{ width: '5%' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inviteLinks
                    .slice(
                      inviteLinksPage * INVITES_PAGE_SIZE,
                      inviteLinksPage * INVITES_PAGE_SIZE + INVITES_PAGE_SIZE
                    )
                    .map((invite) => (
                      <TableRow key={invite._id} hover>
                        <TableCell>
                          <Typography variant="body2">{invite.email || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getLimitedStateLabels(invite.limitAccessToStates)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {invite.createdAt ? new Date(invite.createdAt).toLocaleString() : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Cancel invite" placement="top">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleCancelInvite(invite)}
                                disabled={inviteActionLoading === invite._id}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={inviteLinks.length}
                page={inviteLinksPage}
                onPageChange={(_, page) => setInviteLinksPage(page)}
                rowsPerPage={INVITES_PAGE_SIZE}
                rowsPerPageOptions={[INVITES_PAGE_SIZE]}
              />
            </>
          )}
        </TableContainer>
      )}
    </Wrapper>
  );
};

export default Team;
